<script lang="ts">
  import GdprBanner from '@beyonk/gdpr-cookie-consent-banner'
  import '@beyonk/gdpr-cookie-consent-banner/banner.css'
  import posthog from 'posthog-js'
  import { onMount } from 'svelte'

  interface Props {
    postHogId: string | undefined
    visible: boolean
  }

  const { postHogId, visible }: Props = $props()

  const descriptionHtml =
    'We use cookies to analyze usage and improve your experience. See our <a href="https://www.hirundo.io/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for details.'

  type PosthogWindow = Window & { __posthog_enabled?: boolean }

  function enableAnalytics() {
    console.log('enableAnalytics', postHogId)
    if (!postHogId) {
      return
    }
    if (typeof window !== 'undefined') {
      const w = window as PosthogWindow
      // Avoid re-initializing
      if (!w.__posthog_enabled) {
        posthog.init(postHogId, {
          api_host: 'https://us.i.posthog.com',
          person_profiles: 'always',
          capture_pageview: false,
          capture_pageleave: false,
          disable_persistence: false,
        })
        w.__posthog_enabled = true
      }
    }
  }

  function disableAnalytics() {
    console.log('disableAnalytics', postHogId)
    if (typeof window !== 'undefined') {
      const w = window as PosthogWindow
      if (w.__posthog_enabled) {
        // Opt out and clear any queued events
        try {
          const cfg: Record<string, unknown> = { disable_persistence: true }
          // Disable cookies/localStorage persistence and clear identifiers
          posthog.set_config(cfg)
          posthog.reset()
        } catch {
          // noop
        }
        posthog.opt_out_capturing()
        w.__posthog_enabled = false
      }
    }
  }

  function handleAnalytics(event: CustomEvent<{ agreed: boolean }>) {
    if (event.detail?.agreed) {
      enableAnalytics()
    } else {
      disableAnalytics()
    }
  }

  // The component re-runs opted-in handlers on each visit; on:analytics covers initial load too.
  onMount(() => {})
</script>

<GdprBanner
  {visible}
  cookieName="hirundo_gdpr"
  heading="Cookies and privacy"
  description={descriptionHtml}
  acceptAllLabel="Accept all"
  acceptSelectedLabel="Accept selected"
  rejectLabel="Reject all"
  settingsLabel="Preferences"
  closeLabel="Close"
  editLabel="Edit preferences"
  showEditIcon={true}
  canRejectCookies={false}
  choices={{
    necessary: {
      label: 'Necessary cookies',
      description: "Required for basic functionality (can't be turned off).",
      value: true,
    },
    tracking: false,
    // Combine analytics + marketing into a single consent switch
    analytics: {
      label: 'Analytics and marketing cookies',
      description:
        'Used to understand product usage and improve the experience; also used for marketing-related features.',
      value: true,
    },
    marketing: false,
  }}
  on:analytics={handleAnalytics}
/>

<style>
  :global(.cookieConsentWrapper) {
    background: rgb(var(--color-surface-100));
    color: rgb(var(--color-surface-900));
    border-top: 2px solid rgb(var(--color-primary-500));
  }

  :global(.dark .cookieConsentWrapper) {
    background: rgb(var(--color-surface-900));
    color: rgb(var(--color-surface-50));
  }

  :global(.cookieConsent__Title) {
    color: rgb(var(--color-surface-900));
  }

  :global(.dark .cookieConsent__Title) {
    color: rgb(var(--color-surface-50));
  }

  :global(.cookieConsent__Description) {
    color: rgb(var(--color-surface-700));
  }

  :global(.dark .cookieConsent__Description) {
    color: rgb(var(--color-surface-300));
  }

  :global(.cookieConsent__Description a) {
    color: rgb(var(--color-primary-500));
    text-decoration-color: rgb(var(--color-primary-400));
  }

  :global(.cookieConsent__Button) {
    background: rgb(var(--color-primary-500));
    color: rgb(var(--on-primary));
    border-radius: var(--theme-rounded-base);
  }

  :global(.cookieConsent__Button--Close) {
    background: transparent;
    color: rgb(var(--color-primary-600));
    border: 2px solid rgb(var(--color-primary-600));
  }

  :global(.cookieConsentToggle) {
    background: rgb(var(--color-primary-500));
    color: rgb(var(--on-primary));
  }

  :global(.cookieConsentToggle:hover) {
    background: rgb(var(--color-primary-700));
  }

  :global(.cookieConsentOperations__List) {
    background: rgb(var(--color-surface-100));
    color: rgb(var(--color-surface-900));
    border-radius: var(--theme-rounded-container);
  }

  :global(.dark .cookieConsentOperations__List) {
    background: rgb(var(--color-surface-800));
    color: rgb(var(--color-surface-50));
  }

  /* Toggle track (background) */
  :global(.cookieConsentOperations__Item label::before) {
    background: rgb(var(--color-surface-300));
    border: 2px solid rgb(var(--color-surface-400));
  }

  :global(.dark .cookieConsentOperations__Item label::before) {
    background: rgb(var(--color-surface-700));
    border: 2px solid rgb(var(--color-surface-600));
  }

  /* Toggle knob (circle) - unchecked state */
  :global(.cookieConsentOperations__Item label::after) {
    background: rgb(var(--color-surface-500));
  }

  :global(.dark .cookieConsentOperations__Item label::after) {
    background: rgb(var(--color-surface-400));
  }

  /* Toggle knob (circle) - checked state */
  :global(.cookieConsentOperations__Item input:checked + label::before) {
    background: rgb(var(--color-primary-200));
    border-color: rgb(var(--color-primary-400));
  }

  :global(.dark .cookieConsentOperations__Item input:checked + label::before) {
    background: rgb(var(--color-primary-800));
    border-color: rgb(var(--color-primary-600));
  }

  :global(.cookieConsentOperations__Item input:checked + label::after) {
    background: rgb(var(--color-primary-500));
  }

  /* Item label text */
  :global(.cookieConsentOperations__Item label) {
    color: rgb(var(--color-surface-900));
  }

  :global(.dark .cookieConsentOperations__Item label) {
    color: rgb(var(--color-surface-50));
  }

  /* Item description text */
  :global(.cookieConsentOperations__Item span) {
    color: rgb(var(--color-surface-700));
  }

  :global(.dark .cookieConsentOperations__Item span) {
    color: rgb(var(--color-surface-300));
  }
</style>
